/**
 * AppVersionFooter Component
 *
 * Displays app version number and last-updated timestamp
 * at the bottom of the settings screen.
 */

import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { StyleSheet, Text, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * Formats the last updated date into a readable string.
 */
function formatLastUpdated(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type AppVersionFooterProps = {
  /** Renders right-aligned small text for inline placement in a header row. */
  compact?: boolean;
};

export function AppVersionFooter({ compact = false }: AppVersionFooterProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const version = Constants.expoConfig?.version ?? '0.0.0';
  const lastUpdatedDate = Updates.createdAt;

  const lastUpdatedLabel = lastUpdatedDate
    ? `Last updated ${formatLastUpdated(lastUpdatedDate)}`
    : 'Development build';

  return (
    <View style={compact ? styles.compactContainer : styles.container}>
      <Text style={[compact ? styles.compactVersionText : styles.versionText, { color: palette.textMuted }]}>
        Version {version}
      </Text>
      <Text style={[compact ? styles.compactUpdatedText : styles.updatedText, { color: palette.textMuted }]}>
        {lastUpdatedLabel}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: Spacing.xl,
    paddingHorizontal: Spacing.lg,
  },
  versionText: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 4,
  },
  updatedText: {
    fontSize: 12,
    fontWeight: '400',
  },
  compactContainer: {
    alignItems: 'flex-end',
  },
  compactVersionText: {
    fontSize: 11,
    fontWeight: '500',
  },
  compactUpdatedText: {
    fontSize: 10,
    fontWeight: '400',
    marginTop: 2,
  },
});
